import { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Spin, Alert, Progress, Badge, Descriptions,
} from 'antd';
import {
  CloudServerOutlined, WifiOutlined, DisconnectOutlined, SyncOutlined,
  SafetyCertificateOutlined, DatabaseOutlined,
} from '@ant-design/icons';
import { monitoring } from '../../../services/api';

const CONNECTIVITY_COLOR = { online: 'success', offline: 'error', unknown: 'default' };
const CONNECTIVITY_LABEL = { online: '在线', offline: '离线', unknown: '未知' };

export default function EdgeCollectorPage() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const loadStatus = async () => {
    try {
      const res = await monitoring.edgeStatus();
      setStatus(res.data);
    } catch (_) {
      // 静默刷新
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const timer = setInterval(loadStatus, 5000);
    return () => clearInterval(timer);
  }, []);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const edge = status?.primary_edge || {};
  const localStrategy = edge.local_strategy;
  const bufferPct = edge.queue_depth
    ? Math.min(100, Math.round((edge.queue_depth / 5000) * 100))
    : 0;

  const strategyColumns = [
    { title: '设备', dataIndex: 'meter_name' },
    {
      title: '建议动作',
      dataIndex: 'recommended_action',
      render: (v) => <Tag color={v === '充电' ? 'blue' : v === '放电' ? 'volcano' : 'default'}>{v}</Tag>,
    },
    { title: '原因', dataIndex: 'reason', ellipsis: true },
    {
      title: '风险',
      dataIndex: 'risk_level',
      render: (v) => <Badge status={v === '高' ? 'error' : v === '中' ? 'warning' : 'success'} text={v} />,
    },
  ];

  return (
    <div>
      {edge.connectivity === 'offline' && (
        <Alert
          type="warning"
          showIcon
          icon={<DisconnectOutlined />}
          message="边缘节点与云平台断开，已启用本地安全策略自治运行"
          description="遥测数据正在本地缓冲，网络恢复后将自动批量续传至云端。"
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="边缘节点"
              value={edge.edge_id || '未注册'}
              prefix={<CloudServerOutlined />}
              valueStyle={{ fontSize: 16 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="连接状态"
              value={CONNECTIVITY_LABEL[edge.connectivity] || '未知'}
              prefix={edge.connectivity === 'online' ? <WifiOutlined /> : <DisconnectOutlined />}
              valueStyle={{ color: edge.is_online ? '#52c41a' : '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="缓冲队列"
              value={edge.queue_depth || 0}
              suffix="条"
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: edge.queue_depth > 0 ? '#faad14' : '#52c41a' }}
            />
            <Progress percent={bufferPct} size="small" status={bufferPct > 80 ? 'exception' : 'active'} showInfo={false} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="本轮采集"
              value={edge.stats?.modbus_ok ?? 0}
              suffix={`/ ${status?.meter_stats?.modbus || 0} 表计`}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="边缘运行状态" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="最后采集">{edge.last_poll_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="最后续传">{edge.last_sync_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="心跳更新">{edge.updated_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="采集模式">{status?.server_collector?.mode || 'auto'}</Descriptions.Item>
              <Descriptions.Item label="采集间隔">{status?.server_collector?.interval_ms || 5000} ms</Descriptions.Item>
              <Descriptions.Item label="TSDB">
                <Tag color={status?.tsdb_enabled ? 'green' : 'default'}>
                  {status?.tsdb_enabled ? '已启用' : '未启用'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="断网续传能力" size="small">
            <Row gutter={[8, 8]}>
              {(status?.features
                ? Object.entries(status.features).map(([k, v]) => ({ key: k, enabled: v }))
                : []
              ).map((f) => (
                <Col span={8} key={f.key}>
                  <Tag color={f.enabled ? 'green' : 'default'}>
                    {f.key === 'offline_buffer' ? '本地缓冲' : f.key === 'batch_resume' ? '批量续传' : '本地策略'}
                  </Tag>
                </Col>
              ))}
            </Row>
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="续传机制"
              description="边缘采集服务在断网时将 Modbus 数据写入本地 JSON 队列；恢复连接后通过 POST /monitoring/ingest/batch 按批次上传，并触发告警与 TSDB 补写。"
            />
          </Card>
        </Col>
      </Row>

      {localStrategy && (
        <Card
          title={<><SafetyCertificateOutlined /> 本地安全策略（边缘自治）</>}
          style={{ marginTop: 16 }}
          size="small"
          extra={<Tag color="orange">{localStrategy.mode_label}</Tag>}
        >
          <div style={{ marginBottom: 12, color: '#666' }}>
            评估时间：{localStrategy.evaluated_at}
          </div>
          <Table
            columns={strategyColumns}
            dataSource={(localStrategy.decisions || []).map((d, i) => ({ ...d, key: i }))}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      <Card title="边缘节点列表" style={{ marginTop: 16 }} size="small">
        <Table
          size="small"
          pagination={false}
          dataSource={(status?.edges || []).map((e, i) => ({ ...e, key: i }))}
          columns={[
            { title: '节点ID', dataIndex: 'edge_id' },
            {
              title: '状态',
              dataIndex: 'connectivity',
              render: (v, r) => (
                <Tag color={CONNECTIVITY_COLOR[v] || 'default'}>
                  {r.is_online ? CONNECTIVITY_LABEL[v] || v : '失联'}
                </Tag>
              ),
            },
            { title: '缓冲深度', dataIndex: 'queue_depth', render: (v) => v || 0 },
            { title: '最后采集', dataIndex: 'last_poll_at', render: (v) => v || '-' },
            { title: '最后续传', dataIndex: 'last_sync_at', render: (v) => v || '-' },
          ]}
        />
      </Card>
    </div>
  );
}
